import { SyntheticEvent, useState } from "react";
import { useNavigate } from "react-router";
import NewsArticleDialog from "features/Dashboard/components/NewsArticleDialog/NewsArticleDialog.tsx";
import { useGetNewsArchiveQuery } from "features/Dashboard/api/dashboardApi.ts";
import type { components } from "types/declarations/api";
import * as Style from "./NewsAndUpdates.styles.ts";

const PREVIEW_SIZE = 6;
const FALLBACK_THUMBNAIL = "/assets/No-Photo.svg";
type NewsArchiveArticle = components["schemas"]["NewsArchiveArticle"];

export const NewsAndUpdates = () => {
	const navigate = useNavigate();
	const [selectedArticle, setSelectedArticle] = useState<NewsArchiveArticle | null>(null);
	const { data, isLoading, isFetching, isError } = useGetNewsArchiveQuery({
		pageNumber: 1,
		pageSize: PREVIEW_SIZE
	});
	const articles = data?.items ?? [];

	const handleImageError = (event: SyntheticEvent<HTMLImageElement>) => {
		const image = event.currentTarget;
		if (!image.src.endsWith(FALLBACK_THUMBNAIL)) image.src = FALLBACK_THUMBNAIL;
	};

	const renderNewsCards = () => {
		if (isLoading || isFetching) {
			return (
				<Style.NewsCardGrid
					aria-label="News and updates cards"
					data-testid="news-and-updates-loading-skeleton"
				>
					{Array.from({ length: PREVIEW_SIZE }, (_, index) => (
						<Style.LoadingCard
							key={index}
							variant="rectangular"
						/>
					))}
				</Style.NewsCardGrid>
			);
		}

		if (isError) {
			return null;
		}

		if (articles.length === 0) {
			return <Style.StateMessage severity="info">No news articles are available right now.</Style.StateMessage>;
		}

		return (
			<Style.NewsCardGrid aria-label="News and updates cards">
				{articles.map((article, index) => (
					<Style.NewsCard
						key={`${article.title ?? "article"}-${index}`}
						type="button"
						aria-label={`Open ${article.title ?? "news article"}`}
						onClick={() => setSelectedArticle(article)}
					>
						<Style.NewsCardImage
							src={article.thumbnailUrl || FALLBACK_THUMBNAIL}
							alt=""
							onError={handleImageError}
						/>
						<Style.NewsCardContent>
							<Style.NewsCardTitle>{article.title}</Style.NewsCardTitle>
							<Style.NewsCardSubtitle>{article.subtitle}</Style.NewsCardSubtitle>
						</Style.NewsCardContent>
						<Style.NewsCardChevron aria-hidden="true" />
					</Style.NewsCard>
				))}
			</Style.NewsCardGrid>
		);
	};

	return (
		<>
			<Style.NewsAndUpdatesSection aria-label="News and updates section">
				<Style.NewsAndUpdatesHeader>News & Updates</Style.NewsAndUpdatesHeader>
				<Style.HeaderDivider />
				{renderNewsCards()}
				<Style.NewsArchiveButton
					type="button"
					aria-label="View News Archive"
					onClick={() => navigate("/news-archive")}
				>
					<Style.NewsArchiveButtonText>View News Archive</Style.NewsArchiveButtonText>
					<Style.NewsCardChevron aria-hidden="true" />
				</Style.NewsArchiveButton>
			</Style.NewsAndUpdatesSection>
			<NewsArticleDialog
				article={selectedArticle}
				onClose={() => setSelectedArticle(null)}
			/>
		</>
	);
};

export default NewsAndUpdates;
